import { useState } from 'react';
import { useAppStore } from '@/lib/store';
import { API_URL } from '@/contexts/AuthContext';
import { X, Coffee, Sparkles, Loader2, Link as LinkIcon, AlertCircle } from 'lucide-react';
import type { Chapter } from '@/lib/store';
import TurndownService from 'turndown';

interface PublishModalProps {
    onClose: () => void;
    chapter: Chapter;
    novelId: string;
}

export function PublishModal({ onClose, chapter, novelId }: PublishModalProps) {
    const { themeProfile } = useAppStore();
    const novel = useAppStore(state => state.novels.find(n => n.id === novelId));
    const [loading, setLoading] = useState(false);
    const [publishedId, setPublishedId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [genre, setGenre] = useState('literary');
    const [summary, setSummary] = useState(novel?.description || '');
    const [isFree, setIsFree] = useState(true);
    const [beanCost, setBeanCost] = useState(1);

    const handlePublish = async () => {
        setLoading(true);
        setError(null);

        try {
            if (!novel) throw new Error("Novel not found.");

            // 1. Convert to Markdown
            const turndownService = new TurndownService({
                headingStyle: 'atx',
                hr: '---',
                bulletListMarker: '-',
                codeBlockStyle: 'fenced'
            });

            const markdownTitle = `# ${chapter.title || 'Untitled Chapter'}\n\n`;
            const markdownContent = markdownTitle + turndownService.turndown(chapter.content || '');

            const pubId = `${novelId}-${chapter.id}`;

            // Publish to API-backed Coffee House
            const payload = {
                id: pubId,
                novelId: novel.id,
                chapterId: chapter.id,
                title: novel.title,
                chapterTitle: chapter.title,
                summary: summary.trim(),
                genre,
                tags: [],
                isFree,
                beanCost: isFree ? 0 : Math.max(1, beanCost),
                contentMd: markdownContent,
                contentHtml: chapter.content || '',
            };

            const publishRes = await fetch(`${API_URL}/api/works/publish`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!publishRes.ok) {
                const errData = await publishRes.json().catch(() => ({}));
                throw new Error(errData.error || "Failed to publish work.");
            }

            setPublishedId(pubId);
        } catch (err: any) {
            console.error("Publishing error:", err);
            setError(err.message || 'An error occurred while publishing to The Coffee House.');
        } finally {
            setLoading(false);
        }
    };

    const copyLink = () => {
        if (!publishedId) return;
        const url = `${window.location.origin}/read/${publishedId}`;
        navigator.clipboard.writeText(url);
        // Could add a toast notification here
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div
                className="w-full max-w-lg rounded-2xl shadow-xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
                style={{
                    backgroundColor: themeProfile.paperSurface,
                    border: `1px solid ${themeProfile.border}`,
                    color: themeProfile.text
                }}
            >
                <div className="flex justify-between items-center p-6 border-b" style={{ borderColor: themeProfile.border }}>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: `${themeProfile.accent}15` }}>
                            <Coffee className="w-5 h-5" style={{ color: themeProfile.accent }} />
                        </div>
                        <h2 className="text-xl font-literary font-semibold">Publish to Café</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-black/5 transition-colors"
                    >
                        <X className="w-5 h-5 opacity-60" />
                    </button>
                </div>

                <div className="p-6 md:p-8 space-y-6">
                    {error && (
                        <div className="p-4 rounded-xl flex items-start gap-3 bg-red-50 text-red-600 text-sm border border-red-100">
                            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <p>{error}</p>
                        </div>
                    )}

                    {!publishedId ? (
                        <>
                            <div className="text-center space-y-4">
                                <h3 className="text-2xl font-literary">{chapter.title || 'Untitled Chapter'}</h3>
                                <p className="opacity-70 leading-relaxed text-sm">
                                    Publishing this chapter will create an immutable, public snapshot in The Coffee House.
                                    Readers will be able to discover and read this chapter immediately.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1.5">Genre</label>
                                    <select
                                        value={genre}
                                        onChange={(e) => setGenre(e.target.value)}
                                        className="w-full px-3 py-2 rounded-xl border bg-transparent"
                                        style={{ borderColor: themeProfile.border }}
                                    >
                                        <option value="literary">Literary</option>
                                        <option value="fantasy">Fantasy</option>
                                        <option value="scifi">Sci-Fi</option>
                                        <option value="romance">Romance</option>
                                        <option value="horror">Horror</option>
                                        <option value="mystery">Mystery</option>
                                        <option value="nonfiction">Non-Fiction</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1.5">Summary</label>
                                    <textarea
                                        value={summary}
                                        onChange={(e) => setSummary(e.target.value)}
                                        rows={3}
                                        placeholder="Short blurb for discovery cards"
                                        className="w-full px-3 py-2 rounded-xl border bg-transparent resize-none"
                                        style={{ borderColor: themeProfile.border }}
                                    />
                                </div>

                                <div className="flex flex-wrap items-center gap-4">
                                    <label className="inline-flex items-center gap-2 text-sm font-medium">
                                        <input
                                            type="checkbox"
                                            checked={isFree}
                                            onChange={(e) => setIsFree(e.target.checked)}
                                        />
                                        Free read
                                    </label>
                                    {!isFree && (
                                        <label className="inline-flex items-center gap-2 text-sm font-medium">
                                            <span>Unlock cost</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={99}
                                                value={beanCost}
                                                onChange={(e) => setBeanCost(Number(e.target.value) || 1)}
                                                className="w-20 px-2 py-1 rounded-lg border bg-transparent"
                                                style={{ borderColor: themeProfile.border }}
                                            />
                                            <span>{beanCost === 1 ? 'Coffee' : 'Coffees'}</span>
                                        </label>
                                    )}
                                </div>
                            </div>

                            <div className="pt-6">
                                <button
                                    onClick={handlePublish}
                                    disabled={loading}
                                    className="w-full py-4 rounded-xl font-medium transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                                    style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <Sparkles className="w-5 h-5" />
                                    )}
                                    {loading ? 'Brewing...' : 'Confirm & Publish'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="text-center space-y-6">
                            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center bg-green-100 text-green-600 mb-4">
                                <Sparkles className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-literary">Successfully Published!</h3>
                            <p className="opacity-70">
                                Your chapter is now live from the production API. Share the link below to invite readers.
                            </p>

                            <div
                                className="p-4 rounded-xl flex items-center justify-between gap-4 mt-6"
                                style={{ backgroundColor: themeProfile.background, border: `1px solid ${themeProfile.border}` }}
                            >
                                <p className="text-sm font-medium opacity-80 flex-1 text-left truncate">
                                    {`${window.location.origin}/read/${publishedId}`}
                                </p>
                                <button
                                    onClick={copyLink}
                                    className="p-2 rounded-lg hover:bg-black/5 transition-colors text-sm font-medium flex items-center gap-2 flex-shrink-0"
                                    style={{ color: themeProfile.accent }}
                                >
                                    <LinkIcon className="w-4 h-4" />
                                    Copy
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
